"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

// ── Types ────────────────────────────────────────────────

export interface SessionCustomer {
  id: number;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  hubspot_contact_id: string | null;
  source: "hubspot" | "local";
  customer_type: string | null;
  record_type: string | null;
  lead_status: string | null;
  contact_type: "lead" | "subscriber" | "customer" | "industry" | "ambassador" | "other";
  original_source: string | null;
  lead_lost_reason_id: number | null;
  qualification_status: string | null;
  interest_status: string | null;
  is_hot_lead: boolean;
  is_ghosted: boolean;
  lead_barrier_summary: string | null;
  quote_lost_reason: string | null;
  quote_lost_details: string | null;
  quote_lost_at: string | null;
  lead_source: string | null;
  record_source: string | null;
  record_source_detail_1: string | null;
  boat_of_interest: string | null;
  notes: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  job_title: string | null;
  event: string | null;
  discovery_data: Record<string, unknown> | null;
}

export interface ActiveSession {
  id: number;
  user_id: number;
  customer_id: number;
  session_type: "call" | "interaction";
  phone: string | null;
  direction: "inbound" | "outbound" | "internal";
  started_at: string;
  notes: string | null;
  discovery_answers: Record<string, DiscoveryAnswer> | null;
  customer_edits: Record<string, unknown> | null;
  updated_at: string;
  customer: SessionCustomer;
}

export interface DiscoveryAnswer {
  answer: string;
  checked: boolean;
  updated: string;
}

export interface DiscoveryQuestion {
  id: string;
  text: string;
  type: "text" | "select";
  options?: string[];
  hint?: string;
}

export interface DiscoveryPhase {
  id: string;
  title: string;
  description?: string;
  questions: DiscoveryQuestion[];
}

export interface DiscoveryTemplate {
  version: number;
  phases: DiscoveryPhase[];
  post_call_reminders?: string[];
}

interface StartSessionParams {
  customerId: number;
  sessionType: "call" | "interaction";
  phone?: string | null;
  direction?: string;
}

interface FinishSessionParams {
  notes?: string;
  direction?: string;
  discovery_answers?: Record<string, DiscoveryAnswer>;
  customer_edits?: Record<string, unknown>;
}

interface SessionContextValue {
  session: ActiveSession | null;
  discoveryTemplate: DiscoveryTemplate | null;
  loading: boolean;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  startSession: (params: StartSessionParams) => Promise<ActiveSession>;
  updateSession: (data: Record<string, unknown>) => void;
  finishSession: (data: FinishSessionParams) => Promise<void>;
  abandonSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────

export function SessionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [discoveryTemplate, setDiscoveryTemplate] =
    useState<DiscoveryTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  // Debounced update ref
  const updateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdateRef = useRef<Record<string, unknown>>({});

  // Check for active session on mount
  useEffect(() => {
    if (!user) {
      setSession(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          session: ActiveSession | null;
          discovery_template: DiscoveryTemplate | null;
        }>("/sessions/active");
        if (!cancelled) {
          setSession(res.session);
          setDiscoveryTemplate(res.discovery_template);
        }
      } catch {
        // No active session
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Heartbeat: poll every 10 seconds to detect sessions started/ended elsewhere
  useEffect(() => {
    if (!user) return;

    const check = async () => {
      try {
        const res = await api.get<{
          session: ActiveSession | null;
          discovery_template: DiscoveryTemplate | null;
        }>("/sessions/active");

        setSession((prev) => {
          // Session started on another device
          if (res.session && !prev) {
            setDiscoveryTemplate(res.discovery_template);
            return res.session;
          }
          // Session ended on another device
          if (!res.session && prev) {
            return null;
          }
          return prev;
        });
      } catch {}
    };

    const interval = setInterval(check, 10_000);
    // Also check on window focus
    const handleFocus = () => check();
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [user]);

  // Flush pending updates
  const flushUpdate = useCallback(async () => {
    const data = { ...pendingUpdateRef.current };
    pendingUpdateRef.current = {};
    if (Object.keys(data).length === 0) return;
    try {
      await api.put("/sessions/active", data);
    } catch {}
  }, []);

  // Debounced update (500ms)
  const updateSession = useCallback(
    (data: Record<string, unknown>) => {
      Object.assign(pendingUpdateRef.current, data);

      // Update local state immediately for responsiveness
      setSession((prev) => {
        if (!prev) return prev;
        return { ...prev, ...data } as ActiveSession;
      });

      if (updateTimerRef.current) clearTimeout(updateTimerRef.current);
      updateTimerRef.current = setTimeout(flushUpdate, 500);
    },
    [flushUpdate]
  );

  const startSession = useCallback(
    async (params: StartSessionParams): Promise<ActiveSession> => {
      const res = await api.post<{
        session: ActiveSession;
        discovery_template: DiscoveryTemplate | null;
        existing: boolean;
      }>("/sessions/start", {
        customer_id: params.customerId,
        session_type: params.sessionType,
        phone: params.phone || null,
        direction: params.direction || "outbound",
      });
      setSession(res.session);
      setDiscoveryTemplate(res.discovery_template);
      return res.session;
    },
    []
  );

  const finishSession = useCallback(
    async (data: FinishSessionParams) => {
      // Flush any pending debounced updates first
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
        updateTimerRef.current = null;
      }
      await flushUpdate();

      await api.post("/sessions/active/finish", data);
      setSession(null);
      setModalOpen(false);
    },
    [flushUpdate]
  );

  const abandonSession = useCallback(async () => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
      updateTimerRef.current = null;
    }
    pendingUpdateRef.current = {};

    await api.delete("/sessions/active");
    setSession(null);
    setModalOpen(false);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        session,
        discoveryTemplate,
        loading,
        modalOpen,
        setModalOpen,
        startSession,
        updateSession,
        finishSession,
        abandonSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}
